import { useInView } from 'framer-motion'
import { useRef } from 'preact/hooks'

import { Actions } from './components/Actions'
import { BenefitCards, Benefits } from './components/Benefits'
import { ConsumptionInput } from './components/ConsumptionInput'
import { CostInputs } from './components/CostInputs'
import { Difference } from './components/Difference'
import { EmailCallback } from './components/EmailInput'
import { FootnotesProvider } from './components/FootnoteProvider'
import { Footnotes } from './components/Footnotes'
import { Heading } from './components/Heading'
import { MiniInputs } from './components/MiniInputs'
import { NaturalPowerCalculatorProvider } from './components/NaturalPowerCalculatorProvider'
import { NaturalPowerContribution } from './components/NaturalPowerContribution'
import { StickyPanel } from './components/StickyPanel'
import { buildEmailPayload, EmailPayloadParameters } from './lib/buildEmailPayload'
import { EmailReportContext } from './machines/emailReport.machine'
import { PaymentFrequencyValues, SelectOptions } from './types/global'

export interface AppProps {
  costPerUnit: number
  emissionFactor: number
  carEmissionFactor: number
  gst: number
  initialNaturalPowerContribution: number
  initialCurrentCost: string | number
  initialAnnualConsumption: number
  initialPaymentFrequency: PaymentFrequencyValues
  annualConsumptionOptions: SelectOptions<number, { averageCost: number }>
  paymentFrequencyOptions: SelectOptions<PaymentFrequencyValues>

  paymentFrequencyLabel?: string
  currentCostLabel?: string
  estimatedCostLabel?: string
  estimatedNaturalPowerCostLabel?: string

  contactCallback: () => void
  emailCallback: EmailCallback
  emailInputPlaceholder: string
  disabledMessage: string
  requiredMessage: string
  invalidMessage: string
  successMessage: string
  errorMessage: string
  timeoutMessage: string
  emailTimeout: number
  tooltips: Record<string, string>
  footnotes: Record<string, string>
  benefits: BenefitCards
}

export function App({
  costPerUnit,
  emissionFactor,
  carEmissionFactor,
  gst,
  initialNaturalPowerContribution,
  initialAnnualConsumption,
  initialPaymentFrequency,
  initialCurrentCost,
  paymentFrequencyOptions,
  paymentFrequencyLabel,
  currentCostLabel,
  estimatedCostLabel,
  estimatedNaturalPowerCostLabel,

  annualConsumptionOptions,
  contactCallback,
  footnotes,
  benefits,

  emailCallback,
  emailInputPlaceholder,
  successMessage,
  errorMessage,
  timeoutMessage,
  disabledMessage,
  requiredMessage,
  invalidMessage,
  emailTimeout,
}: AppProps) {
  const consumptionInputRef = useRef<HTMLDivElement>(null)
  const inputsContainerRef = useRef<HTMLDivElement>(null)

  const costInput: EmailPayloadParameters = {
    costPerUnit,
    emissionFactor,
    carEmissionFactor,
    gst,
    annualConsumption: initialAnnualConsumption,
    naturalPowerRatio: initialNaturalPowerContribution,
    paymentFrequency: initialPaymentFrequency,
    currentCost: initialCurrentCost.toString(),
    paymentFrequencyOptions,
    annualConsumptionOptions,
  }
  const emailReport: EmailReportContext = {
    emailAddress: '',
    emailCallback,
    emailInputPlaceholder,
    successMessage,
    errorMessage,
    timeoutMessage,
    disabledMessage,
    requiredMessage,
    invalidMessage,
    emailTimeout,
    payload: buildEmailPayload(costInput),
  }
  const initialValues = useRef({
    costInput,
    emailReport,
  })

  const costInputLabels = {
    paymentFrequencyLabel,
    currentCostLabel,
    estimatedCostLabel,
    estimatedNaturalPowerCostLabel,
  }

  const showSlider = useInView(inputsContainerRef)

  return (
    <NaturalPowerCalculatorProvider initialValues={initialValues.current}>
      <FootnotesProvider footnotes={footnotes}>
        <div className="flex flex-col justify-start w-full font-sans text-xs font-light">
          <div className="w-full bg-navy">
            <div className="container max-w-screen-lg px-5 pt-10 pb-8 mx-auto text-white md:pb-16">
              <div
                className="flex flex-col w-full sm:flex-row sm:items-end sm:gap-x-4 md:gap-x-12"
                ref={inputsContainerRef}>
                <div className="relative flex flex-col flex-1">
                  <Heading
                    title={
                      <>
                        See how much you could offset your CO<sub>2</sub> emissions
                      </>
                    }
                    subtitle="The greater you NaturalPower™, the greater difference you will make."
                  />
                  <NaturalPowerContribution active={showSlider} />
                </div>
                <div className="w-full pt-8 sm:w-80 shrink-0" ref={consumptionInputRef}>
                  <ConsumptionInput options={annualConsumptionOptions} />
                  <div className="hidden mt-4 space-y-6 sm:block">
                    <CostInputs paymentFrequencyOptions={paymentFrequencyOptions} {...costInputLabels} />
                    <Actions contactCallback={contactCallback} />
                  </div>
                </div>
              </div>
              <StickyPanel watch={inputsContainerRef}>
                <MiniInputs scrollTarget={consumptionInputRef} />
              </StickyPanel>
            </div>
          </div>

          <div className="w-full bg-white">
            <div className="container flex-col justify-start max-w-screen-lg px-5 py-8 mx-auto text-black">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-y-9 lg:gap-x-12">
                <Difference />
                <Benefits cards={benefits} />
              </div>
            </div>
          </div>

          <div className="px-5 pt-8 bg-navy pb-14 sm:hidden">
            <h4 className="text-xl font-normal text-white">How does it impact your bill</h4>
            <CostInputs className="mt-8" paymentFrequencyOptions={paymentFrequencyOptions} {...costInputLabels} />
            <Actions className="mt-7" contactCallback={contactCallback} />
          </div>
          <Footnotes />
        </div>
      </FootnotesProvider>
    </NaturalPowerCalculatorProvider>
  )
}
